import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Badge,
    Center,
    Container,
    Loader,
    Paper,
    Stack,
    Text,
    Title,
    ThemeIcon,
} from "@mantine/core";
import {
    IconCheck,
    IconX,
    IconCar,
} from "@tabler/icons-react";

import { getAuthHeaders } from "../../../api/authHelper";

type ParkingQrValidationDto = {
    isValid: boolean;
    message?: string;
    eventName?: string;
    parkingAreaName?: string;
    validDate?: string;
    usedAt?: string;
};

export default function ParkingQrValidationPage() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const { token } = useParams<{ token: string }>();

    const [loading, setLoading] = useState(true);
    const [data, setData] =
        useState<ParkingQrValidationDto | null>(null);

    useEffect(() => {
        let active = true;

        async function validateQr() {
            setLoading(true);
            setData(null);

            if (!apiUrl || !token) {
                setData({
                    isValid: false,
                    message: "Missing token or API URL.",
                });
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(
                    `${apiUrl}/api/parking/qr/validate/${token}`,
                    {
                        method: "POST",
                        headers: getAuthHeaders(),
                    }
                );

                if (!res.ok) {
                    throw new Error(
                        res.status === 401 || res.status === 403
                            ? "Parking staff login required."
                            : `HTTP ${res.status}`
                    );
                }

                const json: ParkingQrValidationDto =
                    await res.json();

                if (active) {
                    setData(json);
                }
            } catch (error: any) {
                if (active) {
                    setData({
                        isValid: false,
                        message:
                            error.message ||
                            "Could not validate QR code.",
                    });
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }

        validateQr();

        return () => {
            active = false;
        };
    }, [apiUrl, token]);

    const isValid = data?.isValid === true;
    const parkingArea =
        data?.parkingAreaName || "VIP Parking";

    return (
        <Container size="sm" py="xl">
            <Paper
                withBorder
                radius="xl"
                p="xl"
                shadow="lg"
                style={{
                    minHeight: 440,
                    backgroundColor: loading
                        ? "#f8f9fa"
                        : isValid
                            ? "#d3f9d8"
                            : "#ffe3e3",
                    border: `3px solid ${loading
                            ? "#dee2e6"
                            : isValid
                                ? "#2f9e44"
                                : "#e03131"
                        }`,
                }}
            >
                {loading ? (
                    <Center mih={350}>
                        <Stack align="center" gap="md">
                            <Loader color="red" size="lg" />

                            <Title order={3} ta="center">
                                CHECKING PARKING ACCESS
                            </Title>

                            <Text c="dimmed">
                                Please wait...
                            </Text>
                        </Stack>
                    </Center>
                ) : (
                    <Stack
                        align="center"
                        justify="center"
                        gap="lg"
                        mih={350}
                    >
                        <ThemeIcon
                            color={isValid ? "green" : "red"}
                            size={100}
                            radius="xl"
                            variant="filled"
                        >
                            {isValid ? (
                                <IconCheck size={64} stroke={3} />
                            ) : (
                                <IconX size={64} stroke={3} />
                            )}
                        </ThemeIcon>

                        <Badge
                            color={isValid ? "green" : "red"}
                            size="xl"
                            variant="filled"
                        >
                            {isValid ? "VALID" : "DENIED"}
                        </Badge>

                        <Title
                            order={1}
                            ta="center"
                            style={{
                                color: isValid
                                    ? "#087f23"
                                    : "#c92a2a",
                                fontWeight: 900,
                            }}
                        >
                            {isValid
                                ? "ACCESS APPROVED"
                                : "ACCESS DENIED"}
                        </Title>

                        {isValid && (
                            <>
                                <IconCar
                                    size={42}
                                    color="#087f23"
                                />

                                <Text
                                    fw={700}
                                    size="lg"
                                    ta="center"
                                >
                                    WELCOME TO
                                </Text>

                                <Title
                                    order={2}
                                    ta="center"
                                    style={{
                                        color: "#087f23",
                                    }}
                                >
                                    {parkingArea.toUpperCase()}
                                </Title>
                            </>
                        )}

                        {data?.eventName && (
                            <Text fw={600} ta="center">
                                {data.eventName}
                            </Text>
                        )}

                        {!isValid && (
                            <Text
                                size="lg"
                                fw={600}
                                ta="center"
                                c="red.9"
                            >
                                {data?.message ||
                                    "Parking access not valid."}
                            </Text>
                        )}
                    </Stack>
                )}
            </Paper>
        </Container>
    );
}